/* eslint-disable react/jsx-props-no-spreading */ // react-dropzone
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Modal from 'react-modal';
import cx from 'classnames';

import { createVideoForProject } from 'actions';
import { usePrevious } from 'javascripts/hooks';
import UploadService from 'services/upload-service';
import VideoService from 'services/video-service';
import { useDropzone } from 'react-dropzone';
import { ActionLink } from 'components/shared';
import CCEvents, { EventNames } from '@companycam/companycam-events';
import { formatBytes } from 'helpers/strings';
import {
  IMAGE_MAX_SIZE_BYTES,
  PhotoFileTypes,
  PhotoAndVideoFileTypes,
} from 'constants/files';

import { EVENT_ADD_WAITING_ASSET } from 'constants/events';
import eventEmitter from 'helpers/event-emitter';
import { uploadFileAndCreateProjectImageV1 } from 'helpers/uploadFileAndCreateProjectImageV1';
import { uploadRequestAndCreateProjectPhoto } from 'helpers/uploadRequestAndCreateProjectPhoto';
import { fileIsPhoto } from 'helpers/fileIsPhoto';
import { fileIsVideo } from 'helpers/fileIsVideo';
import { userHasPermission, MANAGE_BILLING } from 'helpers/permissions';
import { canAccessFeature, FEATURES } from 'helpers/features';
import useCompanyBillingStatus from '../../billing/useCompanyBillingStatus';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_SECURE_UPLOADS_REQUESTS_WEB_FLAG } from 'constants/featureFlags';
import { translate } from 'translations/i18n';

import { AlertIcon } from 'components/shared/DesignSystem/Icons';
import ScreenReaderContent from 'components/shared/DesignSystem/shared/components/ScreenReaderContent';
import { Button } from '@companycam/slab-web';

const PhotoUploadModal = ({
  isOpen = false,
  projectId,
  user: currentUser,
  onPendingUploadCountChange,
  close: onRequestClose,
}) => {
  const [numOfFilesDropped, setNumOfFilesDropped] = useState(0);
  const [numOfFilesUploaded, setNumOfFilesUploaded] = useState(0);
  const [errorMessages, setErrorMessages] = useState([]);
  const [showVideoNotIncludedMessage, setShowVideoNotIncludedMessage] =
    useState(false);
  const [maxFileSizeBytes, setMaxFileSizeBytes] =
    useState(IMAGE_MAX_SIZE_BYTES);
  const [videoMaxDuration, setVideoMaxDuration] = useState(120);

  const dispatch = useDispatch();
  const { featureSet, limitSet } = useCompanyBillingStatus();
  const planIncludesVideo = canAccessFeature(featureSet, FEATURES.VIDEO);
  const planIncludesVideoOptionsVisible = canAccessFeature(
    featureSet,
    FEATURES.VIDEO_OPTIONS_VISIBLE,
  );

  const prevWasOpen = usePrevious(isOpen);
  const prevNumOfFilesDropped = usePrevious(numOfFilesDropped);
  const prevNumOfFilesUploaded = usePrevious(numOfFilesUploaded);

  const flags = useFlags();
  const secureUploadFlagEnabled = flags[LD_SECURE_UPLOADS_REQUESTS_WEB_FLAG];

  useEffect(() => {
    const updateUploadLimits = () => {
      const videoMaxBytes = Math.max(limitSet?.videoMaxBytes, IMAGE_MAX_SIZE_BYTES);
      const videoMaxSeconds = limitSet?.videoMaxSeconds || 120;

      setVideoMaxDuration(videoMaxSeconds);
      setMaxFileSizeBytes(videoMaxBytes);
    };
    if (isOpen && !prevWasOpen) {
      updateUploadLimits();
    }

    const previousPendingUploadCount =
      prevNumOfFilesDropped - prevNumOfFilesUploaded;
    const currentPendingUploadCount = numOfFilesDropped - numOfFilesUploaded;

    if (previousPendingUploadCount !== currentPendingUploadCount) {
      if (typeof onPendingUploadCountChange === 'function') {
        onPendingUploadCountChange(currentPendingUploadCount);
      }
    }
  }, [
    limitSet,
    numOfFilesUploaded,
    isOpen,
    onPendingUploadCountChange,
    prevNumOfFilesUploaded,
    prevNumOfFilesDropped,
    prevWasOpen,
    numOfFilesDropped,
  ]);

  const formatErrorMessageDropzoneRejection = ({ file }) =>
    `File "${
      file.name
    }" failed (wrong type or size exceeds the maximum of ${formatBytes(
      maxFileSizeBytes,
    )}).`;

  const formatErrorDurationExceeded = (file) =>
    `File "${file.name}" failed (exceeds maximum duration of ${videoMaxDuration}s).`;

  const formatErrorMessageGeneric = (file) => `File "${file.name}" failed.`;

  const uploadFiles = async (files) => {
    if (files.length === 0) {
      return;
    }

    const nextFiles = [...files];
    const file = nextFiles.shift();
    let errorMessage = null;
    let warnVideoNotIncluded = false;

    try {
      const uploadService = new UploadService();

      if (fileIsVideo(file)) {
        if (planIncludesVideo) {
          const duration = await new VideoService(file).getDuration();
          if (duration <= videoMaxDuration) {
            const remoteURL = await uploadService.uploadFileToS3(file);
            await dispatch(
              createVideoForProject(remoteURL, projectId, duration),
            );
          } else {
            errorMessage = formatErrorDurationExceeded(file);
            throw new Error(errorMessage);
          }
        } else {
          warnVideoNotIncluded = true;
        }
      } else if (secureUploadFlagEnabled && fileIsPhoto(file)) {
        const response = await uploadRequestAndCreateProjectPhoto(
          file,
          projectId,
        );

        eventEmitter.trigger(EVENT_ADD_WAITING_ASSET, {
          asset_v1_id: response.data.id,
          user_id: currentUser.Id,
          location_id: projectId,
        });
      } else {
        await uploadFileAndCreateProjectImageV1(
          file,
          currentUser.Id,
          projectId,
        );
      }
    } catch (e) {
      if (!errorMessage) {
        errorMessage = formatErrorMessageGeneric(file);
      }
    }

    setNumOfFilesUploaded(
      (_prevNumOfFilesUploaded) => _prevNumOfFilesUploaded + 1,
    );
    setErrorMessages((prevErrorMessages) =>
      errorMessage ? [...prevErrorMessages, errorMessage] : prevErrorMessages,
    );
    setShowVideoNotIncludedMessage(
      (prevShowVideoNotIncludedMessage) =>
        warnVideoNotIncluded || prevShowVideoNotIncludedMessage,
    );

    uploadFiles(nextFiles);
  };

  const handleErrorMessages = (rejectedFiles) => {
    const newErrorMessages = rejectedFiles.map(
      formatErrorMessageDropzoneRejection,
    );
    setErrorMessages([...errorMessages, ...newErrorMessages]);
  };

  const handleInProgressFileCount = (acceptedFiles) => {
    const doResetCounts = prevNumOfFilesDropped === prevNumOfFilesUploaded;
    setNumOfFilesDropped(
      doResetCounts
        ? acceptedFiles.length
        : prevNumOfFilesDropped + acceptedFiles.length,
    );
    setNumOfFilesUploaded(doResetCounts ? 0 : prevNumOfFilesUploaded);
  };

  const onDrop = async (acceptedFiles, rejectedFiles) => {
    // Log the attempt to upload this batch of photos/videos
    if (acceptedFiles.length > 0) {
      CCEvents.logEvent(EventNames.ADD_PHOTO);
    }

    handleErrorMessages(rejectedFiles);

    handleInProgressFileCount(acceptedFiles);

    uploadFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: planIncludesVideoOptionsVisible
      ? PhotoAndVideoFileTypes
      : PhotoFileTypes,
    multiple: true,
    maxSize: maxFileSizeBytes,
    onDrop,
  });

  const renderStatus = () => {
    if (numOfFilesDropped === 0) {
      return (
        <p className="instructions">
          <span>
            {translate('projects.show.photoUploadModal.modalInstructions')}
          </span>
        </p>
      );
    }

    if (numOfFilesUploaded === numOfFilesDropped) {
      return (
        <p
          className="instructions"
          data-testid="photos__upload-modal-instructions-completed"
        >
          <span>
            {translate(
              'projects.show.photoUploadModal.modalInstructionsCompleted',
            )}
          </span>
        </p>
      );
    }

    const uploadProgress = Math.round(
      100 * (numOfFilesUploaded / numOfFilesDropped),
    );
    return (
      <div>
        <div className="upload-progress-bar">
          <span
            style={{
              width: `${uploadProgress}%`,
            }}
          />
        </div>
        <p className="upload-status">
          {translate('projects.show.photoUploadModal.uploading', {
            count: numOfFilesDropped,
          })}
        </p>
      </div>
    );
  };

  const renderErrors = () => {
    const canManageBilling = userHasPermission(currentUser, MANAGE_BILLING);
    const hasErrors = showVideoNotIncludedMessage || errorMessages.length > 0;

    return hasErrors ? (
      <div
        className="error-messages"
        data-testid="photos__upload-modal-error-messages"
      >
        {showVideoNotIncludedMessage ? (
          <div
            className="info-box info-box--yellow info-box--full-width"
            style={{ margin: '24px 0px' }}
          >
            <AlertIcon color="var(--cc_color_utility_caution_darken_15)" />
            <p>
              Your current plan does not include access to uploading
              videos.&nbsp;
              {canManageBilling ? (
                <a href="/billing">Upgrade Plan</a>
              ) : (
                'Contact your company admin to upgrade your plan.'
              )}
            </p>
          </div>
        ) : null}
        <ul>
          {errorMessages.map((errorMessage) => (
            <li key={errorMessage}>{errorMessage}</li>
          ))}
        </ul>
        {errorMessages.length > 0 ? (
          <Button
            onClick={(e) => {
              e.preventDefault();
              setErrorMessages([]);
            }}
            data-testid="photos__upload-modal-dismiss-errors-button"
          >
            {translate('projects.show.photoUploadModal.dismissErrors')}
          </Button>
        ) : null}
      </div>
    ) : null;
  };

  const photoUploadModalClasses = cx('photo-drop-zone', {
    'photo-drop-zone-active': isDragActive,
  });

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onRequestClose}
      contentLabel="Upload a photo"
      overlayClassName="modal"
      className="modal-content-wrap"
    >
      <ActionLink
        className="modal-close-button"
        onClick={onRequestClose}
        role="button"
        data-testid="photos__upload-modal-close-button"
      >
        <ScreenReaderContent>Close photo upload modal</ScreenReaderContent>
        <i className="mdi mdi-close" />
      </ActionLink>
      <h3 className="modal-title">
        {translate('projects.show.photoUploadModal.modalTitle')}
      </h3>
      <div
        {...getRootProps()}
        className={photoUploadModalClasses}
        aria-label="Dropzone for photo uploads"
        name="photo-drop-zone"
      >
        <input
          {...getInputProps()}
          data-testid="photos__upload-modal-dropzone"
        />
        {renderStatus()}
      </div>
      {renderErrors()}
      <p className="instructions tos">
        By uploading this Content, you represent that you have the rights and
        authority to share all information in the Content with CompanyCam,
        including any Personal Information belonging to you or a third party.
        See full&nbsp;
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://companycam.com/terms"
        >
          Terms of Use
        </a>
        .
      </p>
    </Modal>
  );
};

PhotoUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  projectId: PropTypes.number.isRequired,
  onPendingUploadCountChange: PropTypes.func.isRequired,
};

export default PhotoUploadModal;
